/**
 * Walkthrough run state (ProjectTasks #109).
 *
 * Tracks which scenario is active, which step the user is on, and whether
 * the run is paused. Progress persists in localStorage per scenario so a
 * refresh / navigation away resumes exactly where the user left off, and
 * a completed/abandoned run is remembered so the entry banner can offer
 * "Resume" vs "Start".
 *
 * Persistence shape (localStorage key `ppm-walkthrough`):
 *   {
 *     activeScenarioId: string | null,
 *     stepIndex: number,
 *     paused: boolean,
 *     completedScenarioIds: string[],
 *     stepResults: { [scenarioId]: { [stepId]: 'pass' | 'flag' } },
 *     stepNotes:   { [scenarioId]: { [stepId]: string } },
 *   }
 *
 * stepResults is the test-plan layer: steps that declare an `expect` record
 * whether the expected result matched ('pass') or not ('flag'). Results
 * survive completion so the Help Center card can show "N/M checks passed",
 * and are wiped for a scenario when it is (re)started so every run is a
 * fresh test.
 */

import { create } from 'zustand'
import { getScenario } from './scenarios'
import type { WalkthroughScenario, WalkthroughStep } from './types'

const STORAGE_KEY = 'ppm-walkthrough'

export type StepResult = 'pass' | 'flag'

interface PersistedState {
  activeScenarioId: string | null
  stepIndex: number
  paused: boolean
  completedScenarioIds: string[]
  stepResults: Record<string, Record<string, StepResult>>
  /** Free-text note per flagged step ("what did you see instead") — the run
   *  report quotes these. Wiped with stepResults on (re)start. */
  stepNotes: Record<string, Record<string, string>>
}

function loadPersisted(): PersistedState {
  const fallback: PersistedState = {
    activeScenarioId: null,
    stepIndex: 0,
    paused: false,
    completedScenarioIds: [],
    stepResults: {},
    stepNotes: {},
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<PersistedState>
    return {
      activeScenarioId: parsed.activeScenarioId ?? null,
      stepIndex: typeof parsed.stepIndex === 'number' ? parsed.stepIndex : 0,
      paused: parsed.paused ?? false,
      completedScenarioIds: Array.isArray(parsed.completedScenarioIds) ? parsed.completedScenarioIds : [],
      stepResults: parsed.stepResults && typeof parsed.stepResults === 'object' ? parsed.stepResults : {},
      stepNotes: parsed.stepNotes && typeof parsed.stepNotes === 'object' ? parsed.stepNotes : {},
    }
  } catch {
    return fallback
  }
}

function persist(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* ignore quota / disabled storage */
  }
}

interface WalkthroughState extends PersistedState {
  /** Resolved active scenario (null if none running). */
  activeScenario: () => WalkthroughScenario | null
  /** Current step object (null if none running or index out of range). */
  currentStep: () => WalkthroughStep | null
  start: (scenarioId: string) => void
  next: () => void
  /**
   * Record the current step's expected-result check ('pass' = it worked,
   * 'flag' = didn't match), then advance. No-op when nothing is running.
   */
  recordResult: (result: StepResult, note?: string) => void
  prev: () => void
  goToStep: (index: number) => void
  pause: () => void
  resume: () => void
  exit: () => void
  restart: () => void
}

export const useWalkthroughStore = create<WalkthroughState>((set, get) => {
  const initial = loadPersisted()

  const commit = (patch: Partial<PersistedState>) => {
    set(patch as Partial<WalkthroughState>)
    const s = get()
    persist({
      activeScenarioId: s.activeScenarioId,
      stepIndex: s.stepIndex,
      paused: s.paused,
      completedScenarioIds: s.completedScenarioIds,
      stepResults: s.stepResults,
      stepNotes: s.stepNotes,
    })
  }

  // Fresh run = fresh test: drop a scenario's recorded checks + notes, keep others'.
  const withoutResults = (scenarioId: string) => {
    const { [scenarioId]: _dropped, ...rest } = get().stepResults
    return rest
  }
  const withoutNotes = (scenarioId: string) => {
    const { [scenarioId]: _dropped, ...rest } = get().stepNotes
    return rest
  }

  return {
    ...initial,

    activeScenario: () => (get().activeScenarioId ? getScenario(get().activeScenarioId!) ?? null : null),

    currentStep: () => {
      const scn = get().activeScenarioId ? getScenario(get().activeScenarioId!) : null
      if (!scn) return null
      const idx = get().stepIndex
      return scn.steps[idx] ?? null
    },

    start: (scenarioId) => {
      if (!getScenario(scenarioId)) return
      commit({ activeScenarioId: scenarioId, stepIndex: 0, paused: false, stepResults: withoutResults(scenarioId), stepNotes: withoutNotes(scenarioId) })
    },

    recordResult: (result, note) => {
      const scn = get().activeScenarioId ? getScenario(get().activeScenarioId!) : null
      const step = get().currentStep()
      if (!scn || !step) return
      const trimmed = note?.trim()
      commit({
        stepResults: {
          ...get().stepResults,
          [scn.id]: { ...(get().stepResults[scn.id] ?? {}), [step.id]: result },
        },
        ...(trimmed
          ? {
              stepNotes: {
                ...get().stepNotes,
                [scn.id]: { ...(get().stepNotes[scn.id] ?? {}), [step.id]: trimmed },
              },
            }
          : {}),
      })
      get().next()
    },

    next: () => {
      const scn = get().activeScenarioId ? getScenario(get().activeScenarioId!) : null
      if (!scn) return
      const nextIdx = get().stepIndex + 1
      if (nextIdx >= scn.steps.length) {
        // Completed — record it and clear the active run.
        const completed = Array.from(new Set([...get().completedScenarioIds, scn.id]))
        commit({ activeScenarioId: null, stepIndex: 0, paused: false, completedScenarioIds: completed })
        return
      }
      commit({ stepIndex: nextIdx })
    },

    prev: () => {
      const idx = get().stepIndex
      if (idx <= 0) return
      commit({ stepIndex: idx - 1 })
    },

    goToStep: (index) => {
      const scn = get().activeScenarioId ? getScenario(get().activeScenarioId!) : null
      if (!scn) return
      const clamped = Math.max(0, Math.min(index, scn.steps.length - 1))
      commit({ stepIndex: clamped })
    },

    pause: () => commit({ paused: true }),
    resume: () => commit({ paused: false }),
    exit: () => commit({ activeScenarioId: null, stepIndex: 0, paused: false }),
    restart: () => {
      const id = get().activeScenarioId
      if (!id) return
      commit({ stepIndex: 0, paused: false, stepResults: withoutResults(id) })
    },
  }
})
